import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from "react-query";

import { api } from "./apiInstance";
import {
  AccountInsightsFilterView,
  AccountInsightsResponseView,
  AiSummaryResponseView,
  ContactHistoryFilterView,
  ContactHistoryOverview,
  ContactHistoryOverviewFilterView,
  ContactHistoryPageResponse,
  CustomerActivityFilterView,
  CustomerActivityResponseView,
  CustomerAudienceMembershipFilterView,
  CustomerAudienceMembershipView,
  EmailEngagementStatisticsGroupedRequestView,
  EmailEngagementStatisticsGroupedResponseView,
  PNRatioRequestView,
  PNRatioResponseView,
  SegmentFilterView,
  SegmentPageResponse,
  SiteInteractionStatisticsGroupedRequestView,
  SiteInteractionStatisticsGroupedResponseView,
  TopEngagedCampaignsResponseView,
  TopEngagedWorkflowsResponseView,
  TotalOrderRequestView,
  TotalOrderResponseView,
  TotalRevenueRequestView,
  TotalRevenueResponseView,
  TrafficAwarenessStatisticsGroupedRequestView,
  TrafficAwarenessStatisticsGroupedResponseView,
} from "./GeneratedApi";

export interface ErrorEntity {
  errorCode: string;
  errorMessage: string;
}

export interface Error {
  message?: string;
  response?: {
    status?: number;
    data?: {
      message?: string;
      messageDetails?: string;
      errorCodes?: ErrorEntity[];
    };
  };
}
export function usePostEmailEngagementGroupedQuery(
  data: EmailEngagementStatisticsGroupedRequestView,

  options?: UseQueryOptions<EmailEngagementStatisticsGroupedResponseView, Error>
): UseQueryResult<EmailEngagementStatisticsGroupedResponseView, Error> {
  const key = ["POST", "/dashboard/email-engagement", data];
  return useQuery(
    key,
    async () => {
      const result = await api.postEmailEngagementStatisticsGrouped(data);
      return result.data;
    },
    options
  );
}

export function usePostTrafficAwarenessGroupedQuery(
  data: TrafficAwarenessStatisticsGroupedRequestView,

  options?: UseQueryOptions<
    TrafficAwarenessStatisticsGroupedResponseView,
    Error
  >
): UseQueryResult<TrafficAwarenessStatisticsGroupedResponseView, Error> {
  const key = ["POST", "/metrics/overview", data];
  return useQuery(
    key,
    async () => {
      const result = await api.postTrafficAwarenessStatisticsGrouped(data);
      return result.data;
    },
    options
  );
}

export function usePostSiteInteractionGroupedQuery(
  data: SiteInteractionStatisticsGroupedRequestView,

  options?: UseQueryOptions<SiteInteractionStatisticsGroupedResponseView, Error>
): UseQueryResult<SiteInteractionStatisticsGroupedResponseView, Error> {
  const key = ["POST", "/dashboard/site-interaction", data];
  return useQuery(
    key,
    async () => {
      const result = await api.postSiteInteractionStatisticsGrouped(data);
      return result.data;
    },
    options
  );
}

export function useGetTopEngagedCampaignsQuery(
  options?: UseQueryOptions<TopEngagedCampaignsResponseView, Error>
): UseQueryResult<TopEngagedCampaignsResponseView, Error> {
  const key = ["GET", "/dashboard/campaigns/top-engaged"];
  return useQuery(
    key,
    async () => {
      const result = await api.getTopEngagedCampaigns();
      return result.data;
    },
    options
  );
}

export function useGetTopEngagedWorkflowsQuery(
  options?: UseQueryOptions<TopEngagedWorkflowsResponseView, Error>
): UseQueryResult<TopEngagedWorkflowsResponseView, Error> {
  const key = ["GET", "/dashboard/workflows/top-engaged"];
  return useQuery(
    key,
    async () => {
      const result = await api.getTopEngagedWorkflows();
      return result.data;
    },
    options
  );
}

export function useGetPNRatio(
  data: PNRatioRequestView,

  options?: UseQueryOptions<PNRatioResponseView, Error>
): UseQueryResult<PNRatioResponseView, Error> {
  const key = ["POST", "/dashboard/PN-ratio", data];
  return useQuery(
    key,
    async () => {
      const result = await api.getPNRatio(data);
      return result.data;
    },
    options
  );
}

export function usePostAccountInsightsFindFilterQuery(
  data: AccountInsightsFilterView,

  query: { page?: number; size?: number; sort?: string[] },

  options?: UseQueryOptions<AccountInsightsResponseView, Error>
): UseQueryResult<AccountInsightsResponseView, Error> {
  const key = ["POST", "/dashboard/account-insights", data, query];
  return useQuery(
    key,
    async () => {
      const result = await api.postAccountInsightsFindFilter(data, query);
      return result.data;
    },
    options
  );
}

export function usePostCustomerActivityFindFilterQuery(
  data: CustomerActivityFilterView,

  query: { page?: number; size?: number; sort?: string[] },

  options?: UseQueryOptions<CustomerActivityResponseView, Error>
): UseQueryResult<CustomerActivityResponseView, Error> {
  const key = ["POST", "/dashboard/customer-activity", data, query];
  return useQuery(
    key,
    async () => {
      const result = await api.postCustomerActivityFindFilter(data, query);
      return result.data;
    },
    options
  );
}

export function usePostTotalRevenueQuery(
  data: TotalRevenueRequestView,
  options?: UseQueryOptions<TotalRevenueResponseView, Error>
): UseQueryResult<TotalRevenueResponseView, Error> {
  const key = ["POST", "/dashboard/top-revenue", data];
  return useQuery(
    key,
    async () => {
      const result = await api.postTotalRevenue(data);
      return result.data;
    },
    options
  );
}

export function usePostTotalOrdersQuery(
  data: TotalOrderRequestView,
  options?: UseQueryOptions<TotalOrderResponseView, Error>
): UseQueryResult<TotalOrderResponseView, Error> {
  const key = ["POST", "/dashboard/top-order", data];
  return useQuery(
    key,
    async () => {
      const result = await api.postTotalOrders(data);
      return result.data;
    },
    options
  );
}

export function putContactHistoryOverviewQuery(
  data: ContactHistoryOverviewFilterView,
  options?: UseQueryOptions<ContactHistoryOverview, Error>
): UseQueryResult<ContactHistoryOverview, Error> {
  const key = ["PUT", "/contact-history/overview", data];
  return useQuery(
    key,
    async () => {
      const result = await api.putContactHistoryOverview(data);
      return result.data;
    },
    options
  );
}

export function usePutEmailEngagementQuery(
  data: EmailEngagementStatisticsGroupedRequestView,

  options?: UseQueryOptions<EmailEngagementStatisticsGroupedResponseView, Error>
): UseQueryResult<EmailEngagementStatisticsGroupedResponseView, Error> {
  const key = ["PUT", "/contact-history/email-engagement", data];
  return useQuery(
    key,
    async () => {
      const result = await api.putEmailEngagementStatistics(data);
      return result.data;
    },
    options
  );
}

export function usePutContactHistoryFilterQuery(
  data: ContactHistoryFilterView,
  query: {
    page?: number;
    size?: number;
    sort?: string[];
  },
  options?: UseQueryOptions<ContactHistoryPageResponse, Error>
): UseQueryResult<ContactHistoryPageResponse, Error> {
  const key = ["PUT", "/contact-history'", data, query];
  return useQuery(
    key,
    async () => {
      const result = await api.putContactHistoryFilter(data, query);
      return result.data;
    },
    options
  );
}

export function useGetDashboardAiSummaryQuery(
  options?: UseQueryOptions<AiSummaryResponseView, Error>
): UseQueryResult<AiSummaryResponseView, Error> {
  const key = ["GET", "/dashboard/ai-summary"];
  return useQuery(
    key,
    async () => {
      const result = await api.getDashboardAiSummary();
      return result.data;
    },
    options
  );
}

export function usePutCustomerAudienceMembershipQuery(
  data: CustomerAudienceMembershipFilterView,
  options?: UseQueryOptions<CustomerAudienceMembershipView, Error>
): UseQueryResult<CustomerAudienceMembershipView, Error> {
  const key = ["PUT", "/customer/audience-membership", data];
  return useQuery(
    key,
    async () => {
      const result = await api.putCustomerAudienceMembership(data);
      return result.data;
    },
    options
  );
}

export function usePutSegmentFindFilterQuery(
  data: SegmentFilterView,
  query: {
    page?: number;
    size?: number;
    sort?: string[];
    countAllCustomers?: boolean;
  },
  options?: UseQueryOptions<SegmentPageResponse, Error>
): UseQueryResult<SegmentPageResponse, Error> {
  const key = ["PUT", "/segment/find/filter", data, query];
  return useQuery(
    key,
    async () => {
      const result = await api.putSegmentFindFilter(data, query);
      return result.data;
    },
    options
  );
}

export function usePostSegmentCustomerMutation(
  options?: UseMutationOptions<
    void,
    Error,
    { segmentId: string; customerId: string }
  >
): UseMutationResult<void, Error, { segmentId: string; customerId: string }> {
  return useMutation(async ({ segmentId, customerId }) => {
    const result = await api.postSegmentCustomerBySegmentIdAndCustomerId(
      segmentId,
      customerId
    );
    return result.data;
  }, options);
}

export function useDeleteSegmentCustomerMutation(
  options?: UseMutationOptions<
    void,
    Error,
    { segmentId: string; customerId: string }
  >
): UseMutationResult<void, Error, { segmentId: string; customerId: string }> {
  return useMutation(async ({ segmentId, customerId }) => {
    const result = await api.deleteSegmentCustomerBySegmentIdAndCustomerId(
      segmentId,
      customerId
    );
    return result.data;
  }, options);
}
